import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WalletContext } from '../context/WalletContex';
import useBalance from '../hooks/useBalance';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import apiClient from '../utils/apiClient';
import { getExchangeRate } from '../utils/exchangeRates';
import Logo from '../assets/logo.png';
import {
  HiArrowUp, HiArrowDown, HiArrowsRightLeft, HiUsers,
  HiChevronRight, HiArrowPath, HiInformationCircle,
  HiArrowDownLeft, HiArrowUpRight, HiArrowTopRightOnSquare,
  HiShieldExclamation,
} from 'react-icons/hi2';

const ASSETS = [
  { key: 'btc',        chain: 'btc',        label: 'Bitcoin',      symbol: 'BTC',  cls: 'btc',  icon: '₿', cgId: 'bitcoin', decimals: 6, isUsd: false },
  { key: 'trx',        chain: 'trx',        label: 'USDT (TRC20)', symbol: 'USDT', cls: 'usdt', icon: '$', cgId: null,      decimals: 2, isUsd: true  },
  { key: 'trx-native', chain: 'trx-native', label: 'TRON',         symbol: 'TRX',  cls: 'trx',  icon: 'T', cgId: 'tron',   decimals: 2, isUsd: false },
  { key: 'bnb',        chain: 'bnb',        label: 'BNB',          symbol: 'BNB',  cls: 'eth',  icon: 'B', cgId: 'bnb',    decimals: 4, isUsd: false },
];

const SEND_CHAIN = {
  'btc':        'btc',
  'trx':        'trx',
  'trx-native': 'trx-native',
  'bnb':        'bnb',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { wallet } = useContext(WalletContext);

  const isCustodial = wallet?.custodial === true;

  const btcAddr = wallet?.wallets?.btc?.address ?? '';
  const trxAddr = wallet?.wallets?.trx?.address ?? '';
  const bnbAddr = wallet?.wallets?.bnb?.address ?? '';

  const { balance: btcBal,     isLoading: btcLoad,     refresh: refreshBtc    } = useBalance('btc',        btcAddr);
  const { balance: usdtTrxBal, isLoading: usdtTrxLoad, refresh: refreshUsdt   } = useBalance('trx',        trxAddr);
  const { balance: trxBal,     isLoading: trxLoad,     refresh: refreshTrx    } = useBalance('trx-native', trxAddr);
  const { balance: bnbBal,     isLoading: bnbLoad,     refresh: refreshBnb    } = useBalance('bnb',        bnbAddr);

  const [rates, setRates] = useState({ bitcoin: 0, tron: 0, bnb: 0 });
  const [refreshing, setRefreshing]       = useState(false);
  const [recentTxs, setRecentTxs]         = useState([]);
  const [txsLoading, setTxsLoading]       = useState(false);
  const [selectedTx, setSelectedTx]       = useState(null);
  const [pendingDeposits, setPendingDeps] = useState([]);

  useEffect(() => {
    Promise.all([
      getExchangeRate('bitcoin').then(r => ({ bitcoin: r || 0 })),
      getExchangeRate('tron').then(r => ({ tron: r || 0 })),
      getExchangeRate('bnb').then(r => ({ bnb: r || 0 })),
    ]).then(results => setRates(Object.assign({}, ...results)));
  }, []);

  const fetchRecentTxs = useCallback(async () => {
    if (!btcAddr) return;
    setTxsLoading(true);
    try {
      const results = await Promise.allSettled([
        btcAddr ? apiClient.get(`/history/btc/${btcAddr}`).then(r => (r.data.txs || []).map(tx => ({ ...tx, chain: 'btc' }))) : [],
      ]);
      const merged = results
        .filter(r => r.status === 'fulfilled')
        .flatMap(r => r.value)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 5);
      setRecentTxs(merged);
    } catch {
      setRecentTxs([]);
    } finally {
      setTxsLoading(false);
    }
  }, [btcAddr]);

  useEffect(() => { fetchRecentTxs(); }, [fetchRecentTxs]);

  const email = wallet?.email ?? '';
  useEffect(() => {
    if (!email) return;
    const fetch = () =>
      apiClient.get('/wallet/pending-deposits', { params: { email } })
        .then(r => setPendingDeps(r.data.pending || []))
        .catch(() => {});
    fetch();
    const iv = setInterval(fetch, 30_000);
    return () => clearInterval(iv);
  }, [email]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await Promise.all([refreshBtc(), refreshUsdt(), refreshTrx(), refreshBnb(), fetchRecentTxs()]);
    setRefreshing(false);
  };

  useEffect(() => {
    const id = setInterval(() => {
      refreshBtc(); refreshUsdt(); refreshTrx(); refreshBnb(); fetchRecentTxs();
    }, 30_000);
    return () => clearInterval(id);
  }, [refreshBtc, refreshUsdt, refreshTrx, refreshBnb, fetchRecentTxs]);

  const balMap = {
    'btc':        { bal: btcBal,     load: btcLoad,     usdRate: rates.bitcoin },
    'trx':        { bal: usdtTrxBal, load: usdtTrxLoad, usdRate: 1             },
    'trx-native': { bal: trxBal,     load: trxLoad,     usdRate: rates.tron    },
    'bnb':        { bal: bnbBal,     load: bnbLoad,     usdRate: rates.bnb     },
  };

  const visibleAssets = ASSETS;

  const toUsd = (key) => {
    const { bal, usdRate } = balMap[key];
    return Number(bal || 0) * usdRate;
  };

  const totalUsd = visibleAssets.reduce((sum, a) => sum + toUsd(a.key), 0).toFixed(2);
  const anyLoading = btcLoad || usdtTrxLoad || trxLoad || bnbLoad;

  return (
    <div>
      {/* Top bar */}
      <div className="top-bar" style={{ justifyContent: 'space-between' }}>
        <img src={Logo} alt="Ikhua" style={{ width: 32, height: 32, objectFit: 'contain' }} />
        <span style={{ fontSize: '17px', fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>Ikhua</span>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 12, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: refreshing ? 'default' : 'pointer', color: refreshing ? '#4B5170' : '#8B8FA8' }}
        >
          <HiArrowPath size={18} style={{ animation: (refreshing || anyLoading) ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* Total balance hero */}
      <div className="balance-hero">
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#4B5170', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
          Total Balance
        </p>
        <div className="amount">${totalUsd}</div>
        <div className="sub">USD equivalent</div>
      </div>

      {/* KYC banner — shown until identity is verified */}
      {wallet?.kycStatus !== 'approved' && (
        <button
          onClick={() => navigate('/profile')}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            margin: '0 16px 16px', width: 'calc(100% - 32px)',
            background: wallet?.kycStatus === 'pending'
              ? 'rgba(245,158,11,0.08)'
              : wallet?.kycStatus === 'rejected'
              ? 'rgba(239,68,68,0.08)'
              : 'rgba(124,92,246,0.08)',
            border: `1px solid ${
              wallet?.kycStatus === 'pending' ? 'rgba(245,158,11,0.25)'
              : wallet?.kycStatus === 'rejected' ? 'rgba(239,68,68,0.25)'
              : 'rgba(124,92,246,0.25)'
            }`,
            borderRadius: 16, padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
          }}
        >
          <HiShieldExclamation
            size={22}
            style={{ flexShrink: 0, marginTop: 1 }}
            color={wallet?.kycStatus === 'pending' ? '#F59E0B' : wallet?.kycStatus === 'rejected' ? '#EF4444' : '#7C5CF6'}
          />
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: 3 }}>
              {wallet?.kycStatus === 'pending'
                ? 'KYC Under Review'
                : wallet?.kycStatus === 'rejected'
                ? 'KYC Rejected — Re-submit Required'
                : 'Verify Your Identity to Unlock Deposits & Withdrawals'}
            </div>
            <div style={{ fontSize: '12px', color: '#8B8FA8', lineHeight: 1.5 }}>
              {wallet?.kycStatus === 'pending'
                ? 'Your documents are being reviewed. Usually approved within 24 hours.'
                : wallet?.kycStatus === 'rejected'
                ? `Reason: ${wallet?.kycRejectionReason || 'See Profile for details'}. Tap to re-submit.`
                : 'Tap here to complete KYC — takes under 3 minutes.'}
            </div>
          </div>
        </button>
      )}

      {/* Incoming deposits banner */}
      {pendingDeposits.length > 0 && (
        <div style={{ margin: '0 16px 16px' }}>
          {pendingDeposits.map(p => {
            const LABEL = { btc: 'BTC', usdt: 'USDT', trx: 'TRX', bnb: 'BNB' };
            const DP    = { btc: 8, usdt: 6, trx: 6, bnb: 8 };
            const sym   = LABEL[p.coin] ?? p.coin?.toUpperCase();
            const dp    = DP[p.coin] ?? 8;
            const confLabel = p.required > 1
              ? `${p.confirmations}/${p.required} confirmations`
              : 'Waiting for confirmation';
            return (
              <div key={p.txid} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: 'linear-gradient(135deg,rgba(16,185,129,0.1),rgba(16,185,129,0.05))',
                border: '1px solid rgba(16,185,129,0.3)', borderRadius: 16, padding: '12px 16px',
                marginBottom: 8,
              }}>
                {/* Pulsing dot */}
                <div style={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: 'rgba(16,185,129,0.2)',
                    animation: 'pulse-ring 2s cubic-bezier(0.215,0.61,0.355,1) infinite',
                  }} />
                  <div style={{
                    position: 'absolute', inset: 5, borderRadius: '50%', background: '#10B981',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <HiArrowDownLeft size={14} style={{ color: '#fff' }} />
                  </div>
                  <style>{`@keyframes pulse-ring{0%{transform:scale(.85);opacity:.8}70%{transform:scale(1.15);opacity:0}100%{transform:scale(.85);opacity:0}}`}</style>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#10B981' }}>
                    Incoming {sym}
                  </div>
                  <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 1 }}>{confLabel}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#10B981' }}>
                    +{p.amount.toFixed(dp)} {sym}
                  </div>
                  <div style={{ fontSize: '10px', color: '#4B5170', marginTop: 1 }}>Pending</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: 'flex', justifyContent: 'space-around', padding: '4px 20px 24px' }}>
        <QuickAction icon={<HiArrowUp />}        label="Send"    onClick={() => navigate('/send')} />
        <QuickAction icon={<HiArrowDown />}       label="Receive" onClick={() => navigate('/receive')} />
        <QuickAction icon={<HiArrowsRightLeft />} label="Swap"    onClick={() => navigate('/swap')} />
        <QuickAction icon={<HiUsers />}           label="P2P"     onClick={() => navigate('/p2p')} />
      </div>

      {/* Assets */}
      <div className="section-title">Assets</div>
      <div style={{ margin: '0 16px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        {visibleAssets.map((asset, i) => {
          const { bal, load, usdRate } = balMap[asset.key];
          const usdVal = (Number(bal || 0) * usdRate).toFixed(2);
          const balDisplay = load
            ? '—'
            : `${Number(bal || 0).toFixed(asset.decimals)} ${asset.symbol}`;

          return (
            <React.Fragment key={asset.key}>
              {i > 0 && <div className="divider" style={{ margin: '0 14px' }} />}
              <button
                onClick={() => navigate('/send', { state: { chain: SEND_CHAIN[asset.key] } })}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '14px', display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <div className={`coin-icon ${asset.cls}`}>
                  {asset.icon}
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, color: '#fff', fontSize: '15px' }}>{asset.label}</div>
                  <div style={{ fontSize: '12px', color: '#4B5170', marginTop: 2 }}>{asset.symbol}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '15px' }}>
                    {balDisplay}
                  </div>
                  <div style={{ fontSize: '12px', color: '#4B5170', marginTop: 2 }}>
                    {load ? '...' : `$${usdVal}`}
                  </div>
                </div>
                <HiChevronRight size={16} style={{ color: '#4B5170', flexShrink: 0 }} />
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Unsupported tokens notice */}
      <div style={{ margin: '10px 16px 0', display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(75,81,112,0.15)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, padding: '10px 14px' }}>
        <HiInformationCircle size={16} style={{ color: '#4B5170', flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: '11px', color: '#4B5170', lineHeight: 1.6, margin: 0 }}>
          Ikhua supports BTC, USDT (TRC20), TRX and BNB. Other tokens or networks are not tracked here.
        </p>
      </div>

      {/* Recent Activity */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 8px' }}>
        <div className="section-title" style={{ padding: 0, margin: 0 }}>Recent Activity</div>
        <button onClick={() => navigate('/history')} style={{ background: 'none', border: 'none', color: '#7C5CF6', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          See all
        </button>
      </div>

      <div style={{ margin: '0 16px 24px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        {txsLoading && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#4B5170', fontSize: '13px' }}>
            Loading…
          </div>
        )}
        {!txsLoading && recentTxs.length === 0 && (
          <div style={{ padding: '32px 0', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: 8 }}>📭</div>
            <p style={{ color: '#4B5170', fontSize: '14px', margin: 0 }}>No transactions yet</p>
          </div>
        )}
        {!txsLoading && recentTxs.map((tx, i) => {
          const symbol   = tx.chain === 'btc' ? 'BTC' : 'TRX';
          const received = tx.direction === 'received';
          return (
            <React.Fragment key={`${tx.chain}-${tx.hash}`}>
              {i > 0 && <div className="divider" style={{ margin: '0 16px' }} />}
              <button
                onClick={() => setSelectedTx(tx)}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <div className="tx-item" style={{ padding: '14px 16px' }}>
                  <div className={`tx-icon ${received ? 'received' : 'sent'}`}>
                    {received ? <HiArrowDownLeft size={18} /> : <HiArrowUpRight size={18} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
                      {received ? 'Received' : 'Sent'} {symbol}
                    </div>
                    <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 3, fontFamily: 'monospace' }}>
                      {tx.hash.slice(0, 8)}…{tx.hash.slice(-6)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: received ? '#10B981' : '#EF4444' }}>
                      {received ? '+' : '-'}{tx.value} {symbol}
                    </div>
                    <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 2 }}>
                      {new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                  <HiArrowTopRightOnSquare size={14} style={{ color: '#4B5170', flexShrink: 0, marginLeft: 4 }} />
                </div>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <ExplorerModal tx={selectedTx} onClose={() => setSelectedTx(null)} />
    </div>
  );
}

const EXPLORER_META = {
  btc: { name: 'Mempool.space', url: hash => `https://mempool.space/tx/${hash}` },
  trx: { name: 'Tronscan',      url: hash => `https://tronscan.org/#/transaction/${hash}` },
  bnb: { name: 'BscScan',       url: hash => `https://bscscan.com/tx/${hash}` },
};

function ExplorerModal({ tx, onClose }) {
  if (!tx) return null;
  const meta = EXPLORER_META[tx.chain] ?? EXPLORER_META.btc;
  const open = () => {
    window.open(meta.url(tx.hash), '_blank', 'noopener,noreferrer');
    onClose();
  };
  return (
    <Modal open={!!tx} onClose={onClose} title="View on Block Explorer?">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
          You'll be taken to <strong style={{ color: '#fff' }}>{meta.name}</strong> to view this transaction. It will open in your browser.
        </p>
        <div style={{ background: '#1A1748', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: '11px', color: '#4B5170', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Transaction</span>
          <span style={{ fontSize: '12px', color: '#fff', fontFamily: 'monospace', wordBreak: 'break-all' }}>{tx.hash}</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" size="md" full onClick={onClose}>Cancel</Button>
          <Button variant="primary"   size="md" full onClick={open}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Open Explorer <HiArrowTopRightOnSquare size={14} />
            </span>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function QuickAction({ icon, label, onClick }) {
  return (
    <button onClick={onClick} className="quick-action" style={{ background: 'none', border: 'none' }}>
      <div className="quick-action-icon">{icon}</div>
      <span>{label}</span>
    </button>
  );
}
